import { ReactNode, useRef, useState } from "react";

import classNames from "../../utils/classNames";
import {
  PRIORITY_COMPONENTS,
  PriorityBreakdown,
  PriorityTierMeta,
  totalScoreFromBreakdown,
} from "../../utils/partPriority";

interface PartPriorityChipProps {
  /** Total 0-100 score. Falls back to the breakdown sum when omitted. */
  score?: number;
  tier: PriorityTierMeta;
  breakdown: PriorityBreakdown;
  /** Render the chip without the trailing 0-100 number (e.g. compact rows). */
  hideScore?: boolean;
  /** Trailing element rendered to the right of the chip (e.g. progress bar). */
  trailing?: ReactNode;
  className?: string;
}

/**
 * Tier-coloured priority chip with a hover popover that breaks the
 * 0-100 score down into the six contributing components (part value,
 * low stock, sales demand, sell-through, vehicle commonality, listing
 * age adjustment).
 *
 * The popover is rendered inline below the chip rather than via a
 * portal so it follows the row when scrolling. We use a small
 * mouse-leave grace period so flicking onto the popover itself doesn't
 * dismiss it.
 */
export default function PartPriorityChip({
  score,
  tier,
  breakdown,
  hideScore,
  trailing,
  className,
}: PartPriorityChipProps) {
  const [open, setOpen] = useState(false);
  const closeTimerRef = useRef<number | undefined>(undefined);

  const totalScore = score ?? totalScoreFromBreakdown(breakdown);

  const handleEnter = () => {
    if (typeof closeTimerRef.current !== "undefined") {
      window.clearTimeout(closeTimerRef.current);
      closeTimerRef.current = undefined;
    }
    setOpen(true);
  };

  const handleLeave = () => {
    if (typeof closeTimerRef.current !== "undefined") {
      window.clearTimeout(closeTimerRef.current);
    }
    closeTimerRef.current = window.setTimeout(() => setOpen(false), 120);
  };

  return (
    <div
      className={classNames("relative inline-flex items-center gap-1.5", className)}
      onMouseEnter={handleEnter}
      onMouseLeave={handleLeave}
      onFocus={handleEnter}
      onBlur={handleLeave}
    >
      <button
        type="button"
        aria-describedby={open ? "priority-breakdown" : undefined}
        className={classNames(
          "inline-flex cursor-help items-center gap-1 rounded-full px-1.5 py-0.5 text-[10px] font-bold ring-1 ring-inset transition focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500/40",
          tier.chipClass
        )}
        title={`Priority score ${totalScore}/100 — ${tier.label}`}
        onClick={(e) => e.stopPropagation()}
      >
        <span
          className={classNames(
            "inline-block h-1.5 w-1.5 rounded-full",
            tier.segmentClass
          )}
          aria-hidden="true"
        />
        {tier.shortLabel}
        {!hideScore && (
          <span className="tabular-nums opacity-80">{totalScore}</span>
        )}
      </button>

      {trailing}

      {open && (
        <PriorityBreakdownPopover
          score={totalScore}
          tier={tier}
          breakdown={breakdown}
        />
      )}
    </div>
  );
}

interface PriorityBreakdownPopoverProps {
  score: number;
  tier: PriorityTierMeta;
  breakdown: PriorityBreakdown;
}

function PriorityBreakdownPopover({
  score,
  tier,
  breakdown,
}: PriorityBreakdownPopoverProps) {
  return (
    <div
      id="priority-breakdown"
      role="tooltip"
      className="absolute left-0 top-full z-40 mt-2 w-72 cursor-default rounded-lg border border-slate-200 bg-white p-3 text-left shadow-xl ring-1 ring-slate-200"
      onClick={(e) => e.stopPropagation()}
    >
      <div className="flex items-baseline justify-between gap-2 border-b border-slate-100 pb-2">
        <div>
          <p className="text-[10px] font-semibold uppercase tracking-wide text-slate-500">
            Priority score
          </p>
          <p className="mt-0.5 flex items-baseline gap-1">
            <span className="text-xl font-bold tabular-nums text-slate-900">
              {score}
            </span>
            <span className="text-xs text-slate-400">/ 100</span>
          </p>
        </div>
        <span
          className={classNames(
            "inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-bold ring-1 ring-inset",
            tier.chipClass
          )}
        >
          <span
            className={classNames(
              "inline-block h-1.5 w-1.5 rounded-full",
              tier.segmentClass
            )}
            aria-hidden="true"
          />
          {tier.label}
        </span>
      </div>

      <ul className="mt-2 space-y-1.5">
        {PRIORITY_COMPONENTS.map((comp) => {
          const entry = breakdown[comp.key];
          if (!entry) return null;
          return (
            <BreakdownRow
              key={comp.key}
              label={comp.label}
              description={comp.description}
              score={entry.score}
              min={comp.min}
              max={comp.max}
              formattedValue={comp.formatValue(entry.value)}
            />
          );
        })}
      </ul>

      <p className="mt-2 border-t border-slate-100 pt-2 text-[10px] leading-snug text-slate-400">
        Each component contributes between its min and max to the total.
        Adjustments (e.g. listing age) can push the score down.
      </p>
    </div>
  );
}

interface BreakdownRowProps {
  label: string;
  description: string;
  score: number;
  min: number;
  max: number;
  formattedValue: string;
}

function BreakdownRow({
  label,
  description,
  score,
  min,
  max,
  formattedValue,
}: BreakdownRowProps) {
  // Normalise to 0-1 within the component's min/max so the bar fills
  // proportionally even for the negative-capable listing-age component.
  const range = max - min;
  const fillPct = range === 0 ? 0 : ((score - min) / range) * 100;
  const isNegative = score < 0;
  const isPositive = score > 0;

  const barClass = isNegative
    ? "bg-rose-400"
    : isPositive
      ? "bg-emerald-400"
      : "bg-slate-300";

  return (
    <li className="flex flex-col gap-0.5" title={description}>
      <div className="flex items-baseline justify-between gap-2 text-xs">
        <span className="truncate font-medium text-slate-700">{label}</span>
        <span
          className={classNames(
            "tabular-nums font-semibold",
            isNegative
              ? "text-rose-600"
              : isPositive
                ? "text-emerald-700"
                : "text-slate-500"
          )}
        >
          {score > 0 ? `+${score}` : score}
          <span className="ml-0.5 text-[10px] font-normal text-slate-400">
            / {max > 0 ? `+${max}` : max}
            {min < 0 ? ` · min ${min}` : ""}
          </span>
        </span>
      </div>
      <div className="flex items-center gap-2">
        <div
          className="h-1 flex-1 overflow-hidden rounded-full bg-slate-100"
          aria-hidden="true"
        >
          <div
            className={classNames("h-full rounded-full", barClass)}
            style={{
              width: `${Math.max(0, Math.min(100, fillPct))}%`,
            }}
          />
        </div>
        <span className="shrink-0 text-[10px] tabular-nums text-slate-500">
          {formattedValue}
        </span>
      </div>
    </li>
  );
}
